import {
  Box,
  Spacer,
  Flex,
  Skeleton,
  SkeletonText,
  Stack,
  Text,
  useColorModeValue,
  Button,
  Tooltip,
  Collapse,
  Hide,
  Switch,
} from '@chakra-ui/react';

import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState, useMemo, useRef } from 'react';

import axios from 'axios';
import Breadcrumbs from 'components/common/Breadcrumbs';
import QuestItem from 'components/quest/QuestItem';
import VideoSubQuest from 'components/subQuest/VideoSubQuest';

import 'components/common/styles/Scrollbar.css';

import { useDispatch, useSelector } from 'react-redux';

import {
  fetchProgressionByCourseIdAction,
  postProgressionAction,
} from 'store/modules/progression/progression-actions';
import InteractiveContentSubQuest from 'components/subQuest/InteractiveContentSubQuest';
import useSubQuestAutoPlay from 'hooks/useSubQuestAutoPlay';
import { ReducerStatus } from 'utils/constants';
import { removeHtmlTags } from 'utils/textParser';
import QuizSubQuest from 'components/subQuest/QuizSubQuest';
import { ArrowBackIcon, CloseIcon } from '@chakra-ui/icons';
import NarrativeSubQuest from 'components/subQuest/NarrativeSubQuest';
import {
  isAutoPlayEnabled as getAutoPlayPreference,
  setAutoPlayPreference,
  isSubQuestAvailable,
} from 'utils/subQuestNavigation';
import FileSubmissionSubQuest from 'components/subQuest/FileSubmissionSubQuest';
import { handleErrorAndNavigate, createErrorInfo } from 'utils/errorHandler';

function SubQuestPage() {
  const { id, questId, subQuestId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [quests, setQuests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [subQuest, setSubQuest] = useState(null);
  const [code, setCode] = useState(null);
  const [panelVisibility, setPanelVisibility] = useState(true);
  const [isPanelButtonHover, setIsPanelButtonHover] = useState(false);
  const [isAutoPlayEnabled, setIsAutoPlayEnabled] = useState(() =>
    getAutoPlayPreference()
  );
  // Auto-play state
  const [isCompleted, setIsCompleted] = useState(false);
  const autoPlayTriggeredRef = useRef(false);

  // Configuration for ErrorDisplay component - memoized to prevent useEffect dependency changes
  const subQuestEntityConfig = useMemo(
    () => ({
      name: 'Sub-Quest',
      homeRoute: `/courses/${id}`,
      courseId: parseInt(id, 10), // Add course ID for enhanced class access links
    }),
    [id]
  );

  const postProgressionStatus = useSelector(
    (state) => state.progression.postProgressionStatus
  );

  const handleAutoPlayToggle = (e) => {
    const newValue = e.target.checked;
    setIsAutoPlayEnabled(newValue);
    setAutoPlayPreference(newValue);
  };
  const courseOutlines = useSelector(
    (state) => state.progression.courseOutlines
  );

  const questSubQuests = useSelector(
    (state) => state.progression.questSubQuests
  );

  // useMemo prevents infinite re-renders
  const questsWithSubQuests = useMemo(() => {
    return quests.map((quest) => {
      const questSubQuestsData = questSubQuests.find(
        (q) => q.questId === quest.id
      );
      const allSubQuests = questSubQuestsData
        ? questSubQuestsData.subQuests
        : quest.subQuests || [];

      const publishedSubQuests = allSubQuests.filter((subQuestItem) =>
        isSubQuestAvailable(subQuestItem)
      );

      return {
        ...quest,
        subQuests: publishedSubQuests,
      };
    });
  }, [quests, questSubQuests]);

  const { triggerAutoPlay } = useSubQuestAutoPlay({
    courseId: parseInt(id, 10),
    questId: parseInt(questId, 10),
    subQuestId: parseInt(subQuestId, 10),
    quests: questsWithSubQuests,
  });

  useEffect(() => {
    setIsLoading(true);
    const fetchPageData = async () => {
      try {
        const getCourse = await axios.get(`/courses/${id}`);
        setCourse(getCourse.data);

        const getQuests = await axios.get(`/courses/${id}/quests`);
        const questsData = getQuests.data;

        setQuests(questsData);

        const getSubQuest = await axios.get(
          `/courses/${id}/quests/${questId}/sub-quests/${subQuestId}`
        );

        document.title = `Skill Quest | Course - ${getCourse.data.name} - ${getSubQuest.data.name}`;

        const getCode = await axios.post(`/progressions/`, {
          courseId: id,
          questId,
          subQuestId,
        });

        setCode(getCode.data);

        setSubQuest(getSubQuest.data);

        if (courseOutlines.length === 0)
          dispatch(fetchProgressionByCourseIdAction(id));
      } catch (error) {
        // Create standardized error info
        const errorInfo = createErrorInfo(
          error,
          'Sub-Quest Not Found',
          'The sub-quest you are looking for does not exist or may have been removed.'
        );

        // Handle error and navigate using utility function
        handleErrorAndNavigate(
          dispatch,
          navigate,
          location,
          errorInfo,
          subQuestEntityConfig
        );
      }

      setIsLoading(false);
    };

    fetchPageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    questId,
    subQuestId,
    courseOutlines,
    dispatch,
    navigate,
    subQuestEntityConfig,
  ]); // location is intentionally excluded

  useEffect(() => {
    if (
      postProgressionStatus === ReducerStatus.SUCCEEDED &&
      isCompleted &&
      !autoPlayTriggeredRef.current
    ) {
      autoPlayTriggeredRef.current = true;
      triggerAutoPlay();
    }
  }, [postProgressionStatus, isCompleted, triggerAutoPlay]);

  // Reset auto-play state when sub-quest changes
  useEffect(() => {
    if (course && subQuest) {
      setIsCompleted(false);
      autoPlayTriggeredRef.current = false;
    }
  }, [course, subQuest]);

  const handleSubQuestCompletion = () => {
    setIsCompleted(true);
  };

  const handlePostProgression = (progressionData) => {
    return dispatch(postProgressionAction(progressionData));
  };

  return (
    <Flex minH="100vh" ml={{ base: 0, md: 20 }}>
      <Box flex="1" p="0" position="relative">
        <Box pos="absolute" top="2" right="0" zIndex={2}>
          {!panelVisibility && (
            <Button
              onMouseEnter={() => setIsPanelButtonHover(true)}
              onMouseLeave={() => setIsPanelButtonHover(false)}
              onClick={() => setPanelVisibility(true)}
              leftIcon={<ArrowBackIcon />}
              colorScheme="red"
              variant="solid"
              borderRadius={0}
            >
              {isPanelButtonHover && 'Open Panel'}
            </Button>
          )}
        </Box>

        <Flex direction={{ base: 'column', lg: 'row' }}>
          {/* Sub Quest Content */}
          <Box flex="1" h="100%" p={5}>
            <SkeletonText mb={4} noOfLines={1} isLoaded={!isLoading}>
              <Breadcrumbs
                mb={4}
                links={[
                  { name: 'Home', path: '/' },
                  { name: course?.name, path: `/courses/${id}` },
                  {
                    name: subQuest?.name,
                    path: `/courses/${id}/quests/${questId}/sub-quests/${subQuestId}`,
                  },
                ]}
              />
            </SkeletonText>

            <Skeleton w="100%" h="100%" isLoaded={!isLoading}>
              {subQuest && !isLoading && subQuest?.type?.id === 1 && (
                <VideoSubQuest
                  h="100%"
                  course={course}
                  quest={questsWithSubQuests.find(
                    (quest) => quest.id === parseInt(questId, 10)
                  )}
                  subQuest={subQuest}
                  code={code}
                  onComplete={handleSubQuestCompletion}
                  onPostProgression={handlePostProgression}
                />
              )}
              {subQuest && !isLoading && subQuest?.type?.id === 2 && (
                <NarrativeSubQuest
                  h="100%"
                  course={course}
                  quest={questsWithSubQuests.find(
                    (quest) => quest.id === parseInt(questId, 10)
                  )}
                  subQuest={subQuest}
                  code={code}
                  onComplete={handleSubQuestCompletion}
                  onPostProgression={handlePostProgression}
                />
              )}
              {subQuest && !isLoading && subQuest?.type?.id === 10 && (
                <InteractiveContentSubQuest
                  h="100%"
                  course={course}
                  quest={questsWithSubQuests.find(
                    (quest) => quest.id === parseInt(questId, 10)
                  )}
                  subQuest={subQuest}
                  code={code}
                  onComplete={handleSubQuestCompletion}
                  onPostProgression={handlePostProgression}
                />
              )}
              {subQuest && !isLoading && subQuest?.type?.id === 4 && (
                <QuizSubQuest
                  h="100%"
                  course={course}
                  quest={questsWithSubQuests.find(
                    (quest) => quest.id === parseInt(questId, 10)
                  )}
                  subQuest={subQuest}
                  code={code}
                  quests={questsWithSubQuests}
                />
              )}
              {subQuest && !isLoading && subQuest?.type?.id === 5 && (
                <FileSubmissionSubQuest
                  h="100%"
                  course={course}
                  quest={questsWithSubQuests.find(
                    (quest) => quest.id === parseInt(questId, 10)
                  )}
                  subQuest={subQuest}
                  code={code}
                  quests={questsWithSubQuests}
                />
              )}
            </Skeleton>

            <Box>
              <SkeletonText noOfLines={2} isLoaded={!isLoading}>
                {subQuest && subQuest?.type?.id !== 4 && (
                  <Text mt={4} fontSize="lg" fontWeight="bold">
                    {subQuest?.name}
                  </Text>
                )}
                <Text mt={2}>{removeHtmlTags(subQuest?.objective)}</Text>
              </SkeletonText>
            </Box>
          </Box>

          {/* Course Directory  */}
          <Collapse in={panelVisibility} animateOpacity>
            <Box w={{ base: '100%', lg: '350px' }}>
              <Box
                bg={useColorModeValue('white', 'gray.800')}
                color={useColorModeValue('gray.600', 'white')}
                id="subquest-directory-container"
                className={useColorModeValue(
                  'scrollbar-light',
                  'scrollbar-dark'
                )}
                overflowY="scroll"
                h="100vh"
                borderLeftWidth="1px"
              >
                <Flex>
                  <Text my={4} px={2} fontSize="xs" fontWeight="extrabold">
                    COURSE DIRECTORY
                  </Text>{' '}
                  <Spacer />
                  <Switch
                    my={4}
                    px={2}
                    size="sm"
                    fontSize="xs"
                    isChecked={isAutoPlayEnabled}
                    onChange={handleAutoPlayToggle}
                  >
                    Auto-Play
                  </Switch>
                  <Hide below="lg">
                    <Tooltip label="Close panel">
                      <Button
                        size="sm"
                        variant="link"
                        onClick={() => setPanelVisibility(false)}
                        borderRadius={0}
                      >
                        <CloseIcon boxSize={3} />
                      </Button>
                    </Tooltip>
                  </Hide>
                </Flex>

                {isLoading ? (
                  <Stack>
                    <Skeleton mb={2} height="72px" />
                    <Skeleton mb={2} height="72px" />
                    <Skeleton mb={2} height="72px" />
                    <Skeleton mb={2} height="72px" />
                  </Stack>
                ) : (
                  <Box h="250px">
                    {questsWithSubQuests.map((quest) => (
                      <QuestItem
                        key={quest.id}
                        courseId={parseInt(id, 10)}
                        quest={quest}
                        isMinimalMode
                        activeQuestId={parseInt(questId, 10)}
                        activeSubQuestId={parseInt(subQuestId, 10)}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </Collapse>
        </Flex>
      </Box>
    </Flex>
  );
}

export default SubQuestPage;
